import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import PageShell from '@/components/feature/PageShell';
import PageIntro from '@/components/feature/PageIntro';
import FinalCta from '@/components/feature/FinalCta';
import Container from '@/components/base/Container';
import PreviewNotice from '@/components/base/PreviewNotice';
import ReviewCard from '@/components/feature/ReviewCard';
import type { Review } from '@/types/customer';
import { reviews } from '@/mocks/reviews';
import { services } from '@/mocks/services';

export default function ReviewsPage() {
  const [filter, setFilter] = useState('all');
  const all = reviews as Review[];
  const shown = useMemo(() => (filter === 'all' ? all : all.filter((r) => r.serviceSlug === filter)), [all, filter]);
  const options = [{ slug: 'all', short: 'All' }, ...services.map((s) => ({ slug: s.slug, short: s.short }))];

  return (
    <PageShell>
      <PageIntro eyebrow="Customer Reviews" title="Feedback, Shared Openly.">
        This is where customer feedback about MENDWELL visits will be published once a verified review source is connected.
      </PageIntro>

      <section className="bg-background-50 pb-20 md:pb-28">
        <Container>
          <PreviewNotice>
            Real reviews are not connected yet. The cards below are <strong>sample reviews</strong> for design preview only. No ratings, counts or quotes here come from real customers.
          </PreviewNotice>
          <div className="mt-8 flex flex-wrap gap-2" role="group" aria-label="Filter by appliance">
            {options.map((o) => (
              <button
                key={o.slug}
                type="button"
                aria-pressed={filter === o.slug}
                onClick={() => setFilter(o.slug)}
                className={`cursor-pointer whitespace-nowrap rounded-full px-4 h-9 text-[13px] transition-colors ${filter === o.slug ? 'bg-secondary-500 text-white' : 'border border-background-200 bg-white text-foreground-800 hover:border-background-300'}`}
              >
                {o.short}
              </button>
            ))}
          </div>
          {shown.length > 0 ? (
            <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
              {shown.map((r) => <ReviewCard key={r.id} review={r} />)}
            </div>
          ) : (
            <div className="mt-8 rounded-xl border border-background-200 bg-white px-6 py-16 text-center">
              <p className="text-base text-foreground-950">No reviews for this appliance yet.</p>
              <button type="button" onClick={() => setFilter('all')} className="mt-4 cursor-pointer whitespace-nowrap text-sm text-primary-600 mw-link">Show all</button>
            </div>
          )}
          <div className="mt-14 flex flex-col gap-4 border-t border-background-200 pt-8 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-[15px] text-foreground-600">Looking for a specific repair?</p>
            <Link to="/services" className="inline-flex items-center gap-2 text-sm text-foreground-950 mw-link">Browse all services <span className="mw-arrow">&rarr;</span></Link>
          </div>
        </Container>
      </section>
      <FinalCta />
    </PageShell>
  );
}